// Faraday Capsule 3D
// three.js r170 is served from the theme assets; relative paths keep a single module instance
import * as THREE from './three.module.min.js';
import { OrbitControls } from './three-orbit-controls.js';
import { RoomEnvironment } from './three-room-environment.js';
// Canvas text uses the theme font family
const FONT = getComputedStyle(document.getElementById('r3d-cap')).fontFamily || 'sans-serif';
const A = 5.4, R = 2.6, SEG = 20, N = SEG * 4;
const BASE_H = 9.0, COLLAR_TOP = 10.5, LID_H = 11.0;
const W = 0.25;
const CI = 0.45, CW = 0.3;
const FY = 0.8;
const LIFT = 9;
function ring(d) {
const a = Math.max(A - d, 0);
const r = Math.min(Math.max(R - d, 0), a);
const c = a - r;
const out = new Float32Array(N * 2);
const cs = [[c, c], [-c, c], [-c, -c], [c, -c]];
let k = 0;
for (let q = 0; q < 4; q++) for (let i = 0; i < SEG; i++) {
const t = (q + i / (SEG - 1)) * Math.PI / 2;
out[k++] = cs[q][0] + r * Math.cos(t);
out[k++] = cs[q][1] + r * Math.sin(t);
}
return out;
}
const uArc = (() => {
const r0 = ring(0), acc = [0];
let L = 0;
for (let j = 1; j <= N; j++) {
const a = ((j - 1) % N) * 2, b = (j % N) * 2;
L += Math.hypot(r0[b] - r0[a], r0[b + 1] - r0[a + 1]);
acc.push(L);
}
return acc.map(x => x / L);
})();
function sweep(profile) {
const pos = [], uv = [], idx = [], groups = [];
let vlen = 0, prevRow = -1, along = 0;
const addRow = (p, v) => {
const rg = ring(p.d), start = vlen;
for (let j = 0; j <= N; j++) {
const jj = j % N;
pos.push(rg[jj * 2], p.y, rg[jj * 2 + 1]);
uv.push(uArc[j] * 10, v);
}
vlen += N + 1;
return start;
};
for (let i = 0; i < profile.length - 1; i++) {
const p0 = profile[i], p1 = profile[i + 1];
const v0 = along;
along += Math.hypot(p1.d - p0.d, p1.y - p0.y) * 0.25;
const r0 = (prevRow < 0 || p0.crease) ? addRow(p0, v0) : prevRow;
const r1 = addRow(p1, along);
prevRow = r1;
const istart = idx.length;
for (let j = 0; j < N; j++) {
const a0 = r0 + j, a1 = r0 + j + 1, b1 = r1 + j + 1, b0 = r1 + j;
idx.push(a0, b1, a1, a0, b0, b1);
}
groups.push([istart, idx.length - istart, p0.mat || 0]);
}
const g = new THREE.BufferGeometry();
g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
g.setIndex(idx);
for (const [s, c, m] of groups) g.addGroup(s, c, m);
g.computeVertexNormals();
return g;
}
function arc(cx, cy, rad, a0, a1, n, mat) {
const pts = [];
for (let i = 1; i <= n; i++) {
const t = (a0 + (a1 - a0) * i / n) * Math.PI / 180;
pts.push({ d: cx + rad * Math.cos(t), y: cy + rad * Math.sin(t), mat });
}
return pts;
}
const lidProfile = [
{ d: 0.06, y: 0, mat: 0, crease: true },
{ d: 0, y: 0.06, mat: 0 },
{ d: 0, y: LID_H - 0.15, mat: 0 },
...arc(0.15, LID_H - 0.15, 0.15, 180, 90, 6, 0),
{ d: 0.35, y: LID_H, mat: 0, crease: true },
{ d: 0.35, y: LID_H - 0.06, mat: 0, crease: true },
{ d: A, y: LID_H - 0.06, mat: 0, crease: true },
{ d: A, y: LID_H - 0.6, mat: 2, crease: true },
{ d: 1.05 + 0.3, y: LID_H - 0.6, mat: 2 },
...arc(1.05 + 0.3, LID_H - 0.9, 0.3, 90, 180, 6, 2),
{ d: 1.05, y: 0.45, mat: 2, crease: true },
{ d: 0.8, y: 0.45, mat: 2, crease: true },
{ d: 0.8, y: 0.5, mat: 2, crease: true },
{ d: W + 0.18, y: 0.5, mat: 3, crease: true },
{ d: W, y: 0.5, mat: 0, crease: true },
{ d: W, y: 0, mat: 0, crease: true },
{ d: 0.06, y: 0, mat: 0, crease: true },
];
const baseProfile = [
{ d: A, y: 0.06, mat: 0, crease: true },
{ d: 0.35, y: 0.06, mat: 0, crease: true },
{ d: 0.35, y: 0, mat: 0, crease: true },
{ d: 0.15, y: 0, mat: 0 },
...arc(0.15, 0.15, 0.15, -90, -180, 6, 0),
{ d: 0, y: BASE_H - 0.06, mat: 0 },
{ d: 0.06, y: BASE_H, mat: 0, crease: true },
{ d: CI, y: BASE_H, mat: 1, crease: true },
{ d: CI, y: COLLAR_TOP - 0.12, mat: 1 },
...arc(CI + 0.12, COLLAR_TOP - 0.12, 0.12, 180, 90, 4, 1),
{ d: CI + CW - 0.1, y: COLLAR_TOP, mat: 2, crease: true },
{ d: CI + CW - 0.1, y: COLLAR_TOP + 0.05, mat: 2, crease: true },
{ d: CI + CW, y: COLLAR_TOP + 0.05, mat: 2, crease: true },
{ d: CI + CW + 0.35, y: FY, mat: 2, crease: true },
{ d: A, y: FY, mat: 2, crease: true },
{ d: A, y: 0.06, mat: 0, crease: true },
];
function notchPath(s, a, nb, o = 0, r1 = 0.14) {
const k = 0.707 * r1;
s.lineTo(2.6 + o + r1, a);
s.quadraticCurveTo(2.6 + o, a, 2.6 + o - k, a - k);
s.lineTo(1.9 + o + k, nb + k);
s.quadraticCurveTo(1.9 + o, nb, 1.9 + o - r1, nb);
s.lineTo(-1.9 - o + r1, nb);
s.quadraticCurveTo(-1.9 - o, nb, -1.9 - o - k, nb + k);
s.lineTo(-2.6 - o + k, a - k);
s.quadraticCurveTo(-2.6 - o, a, -2.6 - o - r1, a);
}
function plateShape(inset, notch, o = 0) {
const a = A - inset, r = R - inset, c = a - r, nb = a - 0.7 - o;
const s = new THREE.Shape();
s.moveTo(a, -c);
s.absarc(c, c, r, 0, Math.PI / 2, false);
if (notch) notchPath(s, a, nb, o);
s.lineTo(-c, a);
s.absarc(-c, c, r, Math.PI / 2, Math.PI, false);
s.lineTo(-a, -c);
s.absarc(-c, -c, r, Math.PI, Math.PI * 1.5, false);
s.lineTo(c, -a);
s.absarc(c, -c, r, Math.PI * 1.5, Math.PI * 2, false);
s.closePath();
return s;
}
function noiseTexture(size, fn) {
const c = document.createElement('canvas'); c.width = c.height = size;
const ctx = c.getContext('2d'), img = ctx.createImageData(size, size);
for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
const v = fn(x, y), i = (y * size + x) * 4;
img.data[i] = img.data[i + 1] = img.data[i + 2] = v; img.data[i + 3] = 255;
}
ctx.putImageData(img, 0, 0);
const t = new THREE.CanvasTexture(c);
t.wrapS = t.wrapT = THREE.RepeatWrapping;
return t;
}
function grainTexture(size) {
const small = document.createElement('canvas'); small.width = small.height = size / 4;
const sctx = small.getContext('2d'), sim = sctx.createImageData(small.width, small.height);
for (let i = 0; i < sim.data.length; i += 4) { const v = 165 + Math.random() * 90; sim.data[i] = sim.data[i + 1] = sim.data[i + 2] = v; sim.data[i + 3] = 255; }
sctx.putImageData(sim, 0, 0);
const c = document.createElement('canvas'); c.width = c.height = size;
const ctx = c.getContext('2d'); ctx.imageSmoothingEnabled = true; ctx.drawImage(small, 0, 0, size, size);
const img = ctx.getImageData(0, 0, size, size);
for (let i = 0; i < img.data.length; i += 4) { const v = img.data[i] * 0.6 + (150 + Math.random() * 105) * 0.4; img.data[i] = img.data[i + 1] = img.data[i + 2] = v; }
ctx.putImageData(img, 0, 0);
const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t;
}
const speckle = grainTexture(512);
const specklePlate = speckle.clone(); specklePlate.repeat.set(0.25, 0.25); specklePlate.needsUpdate = true;
const rows = Array.from({ length: 256 }, () => 70 + Math.random() * 185);
const brushed = noiseTexture(256, (x, y) => Math.min(255, rows[y] * 0.8 + Math.random() * 60));
const COLORS = { graphite: 0x2d3235, silver: 0x9da3a6, white: 0xf1f1ee, olive: 0x3d4732 };
const paint = new THREE.MeshPhysicalMaterial({ color: COLORS.graphite, roughness: 0.66, metalness: 0.25, roughnessMap: speckle, metalnessMap: speckle, bumpMap: speckle, bumpScale: 0.003, envMapIntensity: 0.7 });
const paintPlate = paint.clone(); paintPlate.roughnessMap = paintPlate.metalnessMap = paintPlate.bumpMap = specklePlate;
const paints = [paint, paintPlate];
const metal = new THREE.MeshStandardMaterial({ color: 0xb2b2ae, metalness: 1, roughness: 0.42, roughnessMap: brushed });
const inner = new THREE.MeshStandardMaterial({ color: 0x0a0b0d, roughness: 0.85, metalness: 0, envMapIntensity: 0.35 });
const fingers = noiseTexture(256, (x, y) => (x % 8 < 5 ? 235 : 120) + Math.random() * 20);
const copper = new THREE.MeshStandardMaterial({ color: 0x5c3f2b, metalness: 1, roughness: 0.7, roughnessMap: fingers, bumpMap: fingers, bumpScale: 0.006, envMapIntensity: 0.3 });
const neoprene = new THREE.MeshStandardMaterial({ color: 0x2a2b2e, roughness: 0.97, roughnessMap: specklePlate, bumpMap: specklePlate, bumpScale: 0.01 });
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
const root = document.getElementById('r3d-cap');
const stage = root.querySelector('.r3d-stage');
const panel = root.querySelector('.r3d-panel');
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.setSize(stage.clientWidth, stage.clientHeight);
renderer.setClearColor(0x000000, 0);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.9;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
stage.appendChild(renderer.domElement);
const aniso = renderer.capabilities.getMaxAnisotropy();
brushed.anisotropy = aniso;
speckle.anisotropy = specklePlate.anisotropy = 1;
const scene = new THREE.Scene();
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
const camera = new THREE.PerspectiveCamera(20, stage.clientWidth / stage.clientHeight, 10, 600);
const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(0, 9.5, 0);
const FREE_TOP = 56;
function mobile() { return stage.clientWidth <= 820; }
function room() { return mobile() ? stage.clientHeight / Math.max(panel.offsetTop - FREE_TOP, 1) : 1; }
function targetShift() { return mobile() ? stage.clientHeight / 2 - (FREE_TOP + panel.offsetTop) / 2 : 0; }
function fitDistance(h = 26, w = 17) {
const t = Math.tan(camera.fov / 2 * Math.PI / 180);
return Math.max(h / 2 / t * room(), w / 2 / (t * camera.aspect)) * 1.15;
}
function viewDist(v) {
if (!v.dist) return fitDistance(v.fitH, v.fitW);
return mobile() ? v.dist * Math.max(1, 1.5 / camera.aspect, room()) : v.dist;
}
function setView(az = 38, el = 22, dist = fitDistance(), ty = 9.5, tx = 0) {
const a = az * Math.PI / 180, e = el * Math.PI / 180;
controls.target.set(tx, ty, 0);
camera.position.set(tx + dist * Math.cos(e) * Math.sin(a), ty + dist * Math.sin(e), dist * Math.cos(e) * Math.cos(a));
controls.update();
}
setView();
window.__view = setView;
window.__open = t => { openT = openTarget = t; };
window.__stats = () => ({ calls: renderer.info.render.calls, tris: renderer.info.render.triangles, geoms: renderer.info.memory.geometries, tex: renderer.info.memory.textures });
window.__vis = () => ({ onScreen, needRender, w: stage.clientWidth, h: stage.clientHeight, hidden: document.hidden });
window.__force = v => { onScreen = v; needRender = true; };
window.__bench = (n = 60) => { const t = performance.now(); for (let i = 0; i < n; i++) renderer.render(scene, camera); return (performance.now() - t) / n; };
window.__fly = flyTo;
window.__lid = v => { lid.visible = v; };
controls.enableDamping = true;
controls.enablePan = false;
controls.minDistance = 32;
controls.maxDistance = 220;
controls.autoRotateSpeed = 0.8;
const key = new THREE.DirectionalLight(0xffffff, 1.5);
key.position.set(10, 22, 7);
key.castShadow = true;
key.shadow.mapSize.set(1024, 1024);
Object.assign(key.shadow.camera, { left: -16, right: 16, top: 16, bottom: -16, near: 1, far: 60 });
key.shadow.bias = -0.0002;
key.shadow.radius = 6;
scene.add(key);
const fill = new THREE.DirectionalLight(0xdfe6ff, 0.35);
fill.position.set(-12, 8, -6);
scene.add(fill);
const rim = new THREE.DirectionalLight(0xffffff, 0.9);
rim.position.set(-8, 14, -16);
scene.add(rim);
const under = new THREE.DirectionalLight(0xffffff, 0.5);
under.position.set(2, -12, 8);
scene.add(under);
const ground = new THREE.Mesh(new THREE.PlaneGeometry(200, 200), new THREE.ShadowMaterial({ opacity: 0.28 }));
ground.rotation.x = -Math.PI / 2;
ground.receiveShadow = true;
scene.add(ground);
const capsule = new THREE.Group();
scene.add(capsule);
const base = new THREE.Mesh(sweep(baseProfile), [paint, metal, inner]);
base.castShadow = base.receiveShadow = true;
capsule.add(base);
const tabShape = new THREE.Shape();
{ const a = A - 0.35, nb = a - 0.7; tabShape.moveTo(-2.6 - 0.14, a); tabShape.lineTo(2.6 + 0.14, a); notchPath(tabShape, a, nb, 0); tabShape.closePath(); }
const tabBottom = new THREE.Mesh(new THREE.ExtrudeGeometry(tabShape, { depth: 0.06, bevelEnabled: false }), paintPlate);
tabBottom.rotation.set(-Math.PI / 2, 0, Math.PI);
capsule.add(tabBottom);
const pad = new THREE.Mesh(new THREE.ShapeGeometry(plateShape(0.35, true, 0), 24), neoprene);
pad.rotation.set(Math.PI / 2, 0, 0);
pad.position.y = 0.012;
capsule.add(pad);
const lid = new THREE.Group();
lid.position.y = BASE_H;
capsule.add(lid);
const lidShell = new THREE.Mesh(sweep(lidProfile), [paint, metal, inner, copper]);
lidShell.castShadow = lidShell.receiveShadow = true;
lid.add(lidShell);
const plate = new THREE.Mesh(new THREE.ExtrudeGeometry(tabShape, { depth: 0.06, bevelEnabled: false }), paintPlate);
plate.rotation.x = -Math.PI / 2;
plate.position.y = LID_H - 0.06;
lid.add(plate);
const grooveShape = plateShape(0.35, true, 0);
grooveShape.holes.push(plateShape(0.40, true, 0.05));
const groove = new THREE.Mesh(new THREE.ShapeGeometry(grooveShape, 24), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3, depthWrite: false }));
groove.rotation.x = -Math.PI / 2;
groove.position.y = LID_H - 0.06 + 0.003;
lid.add(groove);
function engravedText(str, parent, y, down) {
const W = 1024, H = 256, font = '500 110px ' + FONT;
const c = document.createElement('canvas'); c.width = W; c.height = H;
const b = document.createElement('canvas'); b.width = W; b.height = H;
const tex = new THREE.CanvasTexture(c); tex.anisotropy = aniso; tex.colorSpace = THREE.SRGBColorSpace;
const btex = new THREE.CanvasTexture(b);
const draw = () => {
const ctx = c.getContext('2d'); ctx.clearRect(0, 0, W, H);
ctx.font = font; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
ctx.strokeStyle = '#fff'; ctx.lineWidth = 14; ctx.strokeText(str, 512, 132);
ctx.fillStyle = '#fff'; ctx.fillText(str, 512, 132);
ctx.globalCompositeOperation = 'source-atop';
ctx.fillStyle = 'rgba(0,0,0,0.22)'; ctx.fillText(str, 512, 132);
const crescent = (dx, dy, color) => {
const t = document.createElement('canvas'); t.width = W; t.height = H;
const tc = t.getContext('2d');
tc.font = font; tc.textAlign = 'center'; tc.textBaseline = 'middle';
tc.fillStyle = color; tc.fillText(str, 512, 132);
tc.globalCompositeOperation = 'destination-out';
tc.fillText(str, 512 + dx, 132 + dy);
ctx.filter = 'blur(1.2px)'; ctx.drawImage(t, 0, 0); ctx.filter = 'none';
};
crescent(2.5, 3, 'rgba(0,0,0,0.75)');
crescent(-2, -2.5, 'rgba(255,255,255,0.45)');
ctx.globalCompositeOperation = 'source-over';
const bc = b.getContext('2d'); bc.fillStyle = '#fff'; bc.fillRect(0, 0, W, H);
bc.filter = 'blur(1.2px)';
bc.font = font; bc.textAlign = 'center'; bc.textBaseline = 'middle';
bc.fillStyle = '#000'; bc.fillText(str, 512, 132);
bc.filter = 'none';
const img = bc.getImageData(0, 0, W, H);
for (let i = 0; i < img.data.length; i += 4) {
const v = Math.max(0, Math.min(255, img.data[i] + (Math.random() - 0.5) * 36));
img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
}
bc.putImageData(img, 0, 0);
tex.needsUpdate = true; btex.needsUpdate = true;
};
draw(); document.fonts.load(font).then(draw).catch(() => {});
const m = paint.clone();
m.map = tex; m.bumpMap = btex; m.bumpScale = 0.05;
m.transparent = true; m.alphaTest = 0.5; m.depthWrite = false;
paints.push(m);
const mesh = new THREE.Mesh(new THREE.PlaneGeometry(4.0, 1.0), m);
mesh.rotation.x = down ? Math.PI / 2 : -Math.PI / 2;
mesh.position.set(0, y, (down ? 1 : -1) * (A - 0.35 - 0.35));
parent.add(mesh);
return mesh;
}
engravedText('faraday capsule', lid, LID_H + 0.004, false);
engravedText('made by velter', capsule, -0.004, true);
const phones = new THREE.Group();
phones.visible = false;
capsule.add(phones);
const phonesScale = new THREE.Group();
phonesScale.visible = false;
scene.add(phonesScale);
{
const pw = 7.15, ph = 14.7, pr = 1.1, pt = 0.78;
const rrect = (w, h, r) => {
const shp = new THREE.Shape();
shp.moveTo(-w / 2 + r, -h / 2);
shp.lineTo(w / 2 - r, -h / 2); shp.absarc(w / 2 - r, -h / 2 + r, r, -Math.PI / 2, 0, false);
shp.lineTo(w / 2, h / 2 - r); shp.absarc(w / 2 - r, h / 2 - r, r, 0, Math.PI / 2, false);
shp.lineTo(-w / 2 + r, h / 2); shp.absarc(-w / 2 + r, h / 2 - r, r, Math.PI / 2, Math.PI, false);
shp.lineTo(-w / 2, -h / 2 + r); shp.absarc(-w / 2 + r, -h / 2 + r, r, Math.PI, Math.PI * 1.5, false);
return shp;
};
const bodyGeo = new THREE.ExtrudeGeometry(rrect(pw, ph, pr), { depth: pt, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.06, bevelSegments: 3, curveSegments: 16 });
bodyGeo.translate(0, 0, -pt / 2);
const plW = pw - 0.6, plH = 4.3;
const modGeo = new THREE.ExtrudeGeometry(rrect(plW, plH, 1.0), { depth: 0.3, bevelEnabled: true, bevelThickness: 0.08, bevelSize: 0.08, bevelSegments: 3, curveSegments: 14 });
const ringGeo = new THREE.CylinderGeometry(0.9, 0.98, 0.24, 32);
const barrelGeo = new THREE.CylinderGeometry(0.7, 0.7, 0.1, 32);
const glassGeo = new THREE.CylinderGeometry(0.5, 0.5, 0.03, 32);
const irisGeo = new THREE.CylinderGeometry(0.18, 0.18, 0.01, 20);
const flashRingGeo = new THREE.CylinderGeometry(0.34, 0.34, 0.05, 24);
const flashGeo = new THREE.CylinderGeometry(0.24, 0.24, 0.02, 20);
const flashCoreGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.01, 16);
const lidarGeo = new THREE.CylinderGeometry(0.36, 0.36, 0.03, 20);
const micGeo = new THREE.CylinderGeometry(0.07, 0.07, 0.02, 12);
const windowGeo = new THREE.ShapeGeometry(rrect(pw - 0.9, ph - plH - 1.25, 1.0), 14);
const screenGeo = new THREE.ShapeGeometry(rrect(pw - 0.5, ph - 0.5, pr - 0.2), 16);
const suv = screenGeo.attributes.uv;
for (let i = 0; i < suv.count; i++) suv.setXY(i, suv.getX(i) / (pw - 0.5) + 0.5, suv.getY(i) / (ph - 0.5) + 0.5);
const back = new THREE.MeshPhysicalMaterial({ color: 0xa9acae, roughness: 0.5, metalness: 0.6, clearcoat: 0.2, clearcoatRoughness: 0.4 });
const frame = new THREE.MeshStandardMaterial({ color: 0xc4c6c8, metalness: 1, roughness: 0.34 });
const glassWin = new THREE.MeshPhysicalMaterial({ color: 0xc3c6c8, roughness: 0.28, metalness: 0.15, clearcoat: 1, clearcoatRoughness: 0.18 });
const sc = document.createElement('canvas'); sc.width = 480; sc.height = 1024;
const sx = sc.getContext('2d'), sgr = sx.createLinearGradient(310, 0, 170, 1024);
sgr.addColorStop(0, '#a3d4fd'); sgr.addColorStop(1, '#e2f1fd');
sx.fillStyle = sgr; sx.fillRect(0, 0, 480, 1024);
sx.fillStyle = '#000'; sx.beginPath(); sx.arc(185.5, 38.5, 22.5, Math.PI / 2, Math.PI * 1.5); sx.arc(294.5, 38.5, 22.5, -Math.PI / 2, Math.PI / 2); sx.fill();
const screenTex = new THREE.CanvasTexture(sc); screenTex.colorSpace = THREE.SRGBColorSpace; screenTex.anisotropy = aniso;
const screen = new THREE.MeshPhysicalMaterial({ color: 0x000000, emissive: 0xffffff, emissiveMap: screenTex, roughness: 0.1, clearcoat: 1, clearcoatRoughness: 0.05 });
const barrel = new THREE.MeshStandardMaterial({ color: 0x0c0c0e, metalness: 0.7, roughness: 0.35 });
const lensGlass = new THREE.MeshPhysicalMaterial({ color: 0x12102a, roughness: 0.03, metalness: 0.1, clearcoat: 1, clearcoatRoughness: 0.02 });
const iris = new THREE.MeshStandardMaterial({ color: 0x4a3a9a, roughness: 0.3, emissive: 0x241a60, emissiveIntensity: 0.4 });
const flashRingMat = new THREE.MeshStandardMaterial({ color: 0xc9cbcd, metalness: 1, roughness: 0.3 });
const flashMat = new THREE.MeshStandardMaterial({ color: 0xf0efe9, roughness: 0.45 });
const flashCoreMat = new THREE.MeshStandardMaterial({ color: 0xdfe7ff, roughness: 0.3, emissive: 0x9fb4ff, emissiveIntensity: 0.25 });
const darkMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0c, roughness: 0.55 });
function makePhone(cams) {
const g = new THREE.Group();
const body = new THREE.Mesh(bodyGeo, [back, frame]); body.castShadow = true; g.add(body);
const fz = pt / 2 + 0.06;
const scr = new THREE.Mesh(screenGeo, screen); scr.position.z = -fz - 0.003; scr.rotation.y = Math.PI; g.add(scr);
const mod = new THREE.Mesh(modGeo, back); mod.position.set(0, ph / 2 - 0.35 - plH / 2, fz - 0.03); g.add(mod);
if (!cams) return g;
const win = new THREE.Mesh(windowGeo, glassWin); win.position.set(0, -ph / 2 + 0.45 + (ph - plH - 1.25) / 2, fz + 0.004); g.add(win);
const zTop = fz - 0.03 + 0.3;
for (const [lx, ly] of [[-2.15, 1.0], [-2.15, -1.0], [-0.45, 0]]) {
const cx = mod.position.x + lx, cy = mod.position.y + ly;
const ring = new THREE.Mesh(ringGeo, back); ring.rotation.x = Math.PI / 2; ring.position.set(cx, cy, zTop + 0.11); g.add(ring);
const br = new THREE.Mesh(barrelGeo, barrel); br.rotation.x = Math.PI / 2; br.position.set(cx, cy, zTop + 0.22 + 0.05); g.add(br);
const gl = new THREE.Mesh(glassGeo, lensGlass); gl.rotation.x = Math.PI / 2; gl.position.set(cx, cy, zTop + 0.27 + 0.02); g.add(gl);
const ir = new THREE.Mesh(irisGeo, iris); ir.rotation.x = Math.PI / 2; ir.position.set(cx, cy, zTop + 0.29 + 0.006); g.add(ir);
}
const zPl = zTop + 0.08, fx = mod.position.x + 2.45, fy = mod.position.y + 1.0;
const fr = new THREE.Mesh(flashRingGeo, flashRingMat); fr.rotation.x = Math.PI / 2; fr.position.set(fx, fy, zPl + 0.025); g.add(fr);
const fl = new THREE.Mesh(flashGeo, flashMat); fl.rotation.x = Math.PI / 2; fl.position.set(fx, fy, zPl + 0.05 + 0.01); g.add(fl);
const fc = new THREE.Mesh(flashCoreGeo, flashCoreMat); fc.rotation.x = Math.PI / 2; fc.position.set(fx, fy, zPl + 0.06 + 0.006); g.add(fc);
const ld = new THREE.Mesh(lidarGeo, darkMat); ld.rotation.x = Math.PI / 2; ld.position.set(fx, mod.position.y - 1.0, zPl + 0.015); g.add(ld);
const mc = new THREE.Mesh(micGeo, darkMat); mc.rotation.x = Math.PI / 2; mc.position.set(fx, mod.position.y, zPl + 0.01); g.add(mc);
return g;
}
for (let i = 0; i < 6; i++) {
const p1 = makePhone(i === 5); p1.position.set(0, FY + 0.4 + ph / 2, -3.72 + i * 1.24); phones.add(p1);
const p2 = makePhone(i === 0); p2.rotation.y = Math.PI; p2.position.set(11, ph / 2 + 0.02, -3.1 + i * 1.24); phonesScale.add(p2);
}
}
const LW = 1024, LH = 512;
const logoFinal = document.createElement('canvas'); logoFinal.width = LW; logoFinal.height = LH;
const logoCanvas = document.createElement('canvas'); logoCanvas.width = LW; logoCanvas.height = LH;
const logoTex = new THREE.CanvasTexture(logoCanvas); logoTex.anisotropy = aniso; logoTex.colorSpace = THREE.SRGBColorSpace;
function drawLogoFinal(lines) {
const ctx = logoFinal.getContext('2d'); ctx.clearRect(0, 0, LW, LH);
ctx.font = '500 150px ' + FONT; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
const y0 = LH / 2 - (lines.length - 1) * 85;
ctx.fillStyle = '#cdcbc4';
laserLines = lines.map((t, i) => { const w = ctx.measureText(t).width; return { y: y0 + i * 170, x0: LW / 2 - w / 2, x1: LW / 2 + w / 2 }; });
lines.forEach((t, i) => ctx.fillText(t, LW / 2, y0 + i * 170));
ctx.globalCompositeOperation = 'source-atop';
for (let i = 0; i < 70000; i++) {
const g = 185 + Math.random() * 55;
ctx.fillStyle = 'rgba(' + (g + 2) + ',' + g + ',' + (g - 5) + ',0.35)';
ctx.fillRect(Math.random() * LW, Math.random() * LH, 1.5, 1.5);
}
ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(90,90,88,0.25)';
lines.forEach((t, i) => ctx.strokeText(t, LW / 2, y0 + i * 170));
ctx.globalCompositeOperation = 'source-over';
if (!laser) { const d = logoCanvas.getContext('2d'); d.clearRect(0, 0, LW, LH); d.drawImage(logoFinal, 0, 0); logoTex.needsUpdate = true; }
}
const LOGO_LINES = ['Your', 'logo'];
let laser = null, laserLines = [];
drawLogoFinal(LOGO_LINES);
document.fonts.load('500 150px ' + FONT).then(() => drawLogoFinal(LOGO_LINES)).catch(() => {});
const logoMats = [];
function logoMesh(w, parent, pos, rot) {
const m = new THREE.MeshStandardMaterial({ map: logoTex, transparent: true, opacity: 0, alphaTest: 0.02, roughness: 0.96, metalness: 0.04, envMapIntensity: 0.5, depthWrite: false });
logoMats.push(m);
const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, w / 2), m);
mesh.position.set(...pos); mesh.rotation.set(...rot);
parent.add(mesh);
return mesh;
}
const logoTop = logoMesh(5.0, lid, [0, LID_H - 0.06 + 0.004, 0.2], [-Math.PI / 2, 0, 0]);
const sideZones = [[logoMesh(4.6, lid, [0, 6.3, A + 0.004], [0, 0, 0]), logoMesh(4.6, capsule, [0, 4.8, A + 0.004], [0, 0, 0])]];
const allLogos = [logoTop, ...sideZones.flat()];
const POS = [
{ name: 'Top', view: 'engr' },
{ name: 'Side, top', side: 0, zone: 0, az: 12, el: 12, dist: 48, ty: 13.5, open: 0 },
{ name: 'Side, bottom', side: 0, zone: 1, az: 12, el: 6, dist: 48, ty: 7.5, open: 0 },
];
let posIdx = 0;
function posView() { const pz = POS[posIdx]; return pz.view ? VIEWS[pz.view] : pz; }
function applyPos() {
const pz = POS[posIdx];
allLogos.forEach(m => m.visible = false);
if (pz.side === undefined) logoTop.visible = true; else sideZones[pz.side][pz.zone].visible = true;
const pname = root.querySelector('#r3d-pname'); if (pname) pname.textContent = pz.name;
}
let logoAlpha = 0, logoTarget = 0;
const sparkTex = (() => {
const c = document.createElement('canvas'); c.width = c.height = 64;
const g = c.getContext('2d'), r = g.createRadialGradient(32, 32, 0, 32, 32, 32);
r.addColorStop(0, 'rgba(255,255,255,1)'); r.addColorStop(0.25, 'rgba(215,235,255,0.9)');
r.addColorStop(0.6, 'rgba(120,180,255,0.35)'); r.addColorStop(1, 'rgba(80,140,255,0)');
g.fillStyle = r; g.fillRect(0, 0, 64, 64);
const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
})();
const flashes = allLogos.map(mesh => {
const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: sparkTex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, depthTest: false, opacity: 0 }));
sprite.scale.set(1.6, 1.6, 1); scene.add(sprite);
return { mesh, sprite };
});
const laserLight = new THREE.PointLight(0xa8ccff, 0, 2.5, 2);
scene.add(laserLight);
const NS = 120, sparkPos = new Float32Array(NS * 3), sparkVel = new Float32Array(NS * 3), sparkLife = new Float32Array(NS);
sparkPos.fill(9999);
const sparkGeo = new THREE.BufferGeometry();
sparkGeo.setAttribute('position', new THREE.BufferAttribute(sparkPos, 3));
const sparks = new THREE.Points(sparkGeo, new THREE.PointsMaterial({ map: sparkTex, color: 0xd6e8ff, size: 0.22, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
sparks.frustumCulled = false; scene.add(sparks);
let sparkIdx = 0, sparksAlive = false;
const _v = new THREE.Vector3(), _n = new THREE.Vector3(), _t = new THREE.Vector3();
function emitSparks(wp, n, count) {
for (let i = 0; i < count; i++) {
const j = sparkIdx = (sparkIdx + 1) % NS;
sparkPos[j * 3] = wp.x; sparkPos[j * 3 + 1] = wp.y; sparkPos[j * 3 + 2] = wp.z;
_t.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(3 + Math.random() * 6);
_t.addScaledVector(n, 7 + Math.random() * 10);
sparkVel[j * 3] = _t.x; sparkVel[j * 3 + 1] = _t.y; sparkVel[j * 3 + 2] = _t.z;
sparkLife[j] = 0.25 + Math.random() * 0.3;
}
}
function sparksFrame(dt) {
let any = false;
sparksAlive = false;
for (let j = 0; j < NS; j++) {
if (sparkLife[j] <= 0) continue;
any = true;
sparkLife[j] -= dt;
sparkVel[j * 3 + 1] -= 60 * dt;
sparkPos[j * 3] += sparkVel[j * 3] * dt; sparkPos[j * 3 + 1] += sparkVel[j * 3 + 1] * dt; sparkPos[j * 3 + 2] += sparkVel[j * 3 + 2] * dt;
if (sparkLife[j] <= 0) sparkPos[j * 3] = sparkPos[j * 3 + 1] = sparkPos[j * 3 + 2] = 9999;
}
if (any) sparkGeo.attributes.position.needsUpdate = true;
sparksAlive = any;
}
let laserPending = false;
function armLaser() {
laser = null; laserPending = true;
logoCanvas.getContext('2d').clearRect(0, 0, LW, LH);
logoTex.needsUpdate = true;
}
function showLogoNow() {
laser = null; laserPending = false;
const d = logoCanvas.getContext('2d'); d.clearRect(0, 0, LW, LH); d.drawImage(logoFinal, 0, 0);
logoTex.needsUpdate = true;
flashes.forEach(f => f.sprite.material.opacity = 0);
laserLight.intensity = 0;
}
function stopLaser() {
laser = null; laserPending = false;
flashes.forEach(f => f.sprite.material.opacity = 0);
laserLight.intensity = 0;
sparkLife.fill(0); sparkPos.fill(9999); sparkGeo.attributes.position.needsUpdate = true;
}
function startLaser() {
laserPending = false;
laser = { start: performance.now(), dur: 2400 };
logoCanvas.getContext('2d').clearRect(0, 0, LW, LH);
logoTex.needsUpdate = true;
}
function laserFrame(now) {
if (!laser) return;
const t = (now - laser.start) / laser.dur, k = Math.min(t, 1);
const total = laserLines.reduce((a, l) => a + (l.x1 - l.x0), 0) || 1;
let left = k * total, li = 0, x = 0, y = LH / 2;
for (li = 0; li < laserLines.length; li++) {
const l = laserLines[li], w = l.x1 - l.x0;
if (left <= w || li === laserLines.length - 1) { x = l.x0 + Math.min(left, w); y = l.y; break; }
left -= w;
}
const ctx = logoCanvas.getContext('2d');
ctx.clearRect(0, 0, LW, LH);
for (let i = 0; i < li; i++) ctx.drawImage(logoFinal, 0, laserLines[i].y - 85, LW, 170, 0, laserLines[i].y - 85, LW, 170);
ctx.drawImage(logoFinal, 0, y - 85, x + 3, 170, 0, y - 85, x + 3, 170);
y += Math.sin(now * 0.05) * 40;
let lit = false;
for (const f of flashes) {
if (!f.mesh.visible || k >= 1) { f.sprite.material.opacity = 0; continue; }
const w = f.mesh.geometry.parameters.width;
f.mesh.updateWorldMatrix(true, false);
_v.set((x / LW - 0.5) * w, (0.5 - y / LH) * w / 2, 0.03);
f.mesh.localToWorld(_v);
_n.set(0, 0, 1).transformDirection(f.mesh.matrixWorld);
f.sprite.position.copy(_v).addScaledVector(_n, 0.15);
f.sprite.material.opacity = 0.5 + Math.random() * 0.5;
f.sprite.scale.setScalar(0.6 + Math.random() * 0.7);
if (Math.random() < 0.6) emitSparks(_v, _n, 2);
if (!lit) { laserLight.position.copy(_v).addScaledVector(_n, 0.5); laserLight.intensity = 1.5 + Math.random(); lit = true; }
}
if (!lit) laserLight.intensity = 0;
logoTex.needsUpdate = true;
if (t > 1.6) {
laser = null;
ctx.clearRect(0, 0, LW, LH); ctx.drawImage(logoFinal, 0, 0);
logoTex.needsUpdate = true;
}
}
function tintLogo(key) { const light = key === 'silver' || key === 'white'; logoMats.forEach(m => m.color.setHex(light ? 0x9a9ea1 : 0xbcbab6)); }
tintLogo('graphite');
let openT = 0, openTarget = 0;
const openBtn = root.querySelector('#r3d-open');
function syncToggle() { openBtn.textContent = openTarget > 0.5 ? 'Close' : 'Open'; }
openBtn.addEventListener('click', () => flyTo('home', openTarget > 0.5 ? VIEWS.home : VIEWS.inside));
let shiftY = 0, shiftTarget = 0;
function applyOffset() {
const w = stage.clientWidth, h = stage.clientHeight;
if (w > 820) camera.setViewOffset(w, h, -w * 0.15, 0, w, h); else camera.setViewOffset(w, h, 0, shiftY, w, h);
}
shiftY = shiftTarget = targetShift();
applyOffset();
document.fonts.ready.then(() => { shiftTarget = targetShift(); });
const VIEWS = {
home:    { az: 38, el: 22, dist: null, ty: 9.5, open: 0 },
engr:    { az: -6, el: 50, dist: 24, ty: 20.4, open: 0 },
inside:  { az: 30, el: 26, dist: 46, ty: 11, open: 1 },
bottom:  { az: 35, el: -45, dist: 38, ty: 1.5, open: 0, m: { dist: 32 } },
size:    { az: 30, el: 12, dist: null, fitH: 27, fitW: 47, ty: 9, open: 0, m: { tx: 5, fitH: 22, fitW: 26 } },
};
function applyZoomLimit() { controls.maxDistance = mobile() ? 220 : viewDist(VIEWS.size) * 1.15; }
applyZoomLimit();
// Wheel zooms only over the capsule; elsewhere in the stage the page keeps scrolling
let zoomAllowed = true;
const capBox = new THREE.Box3(), capCorner = new THREE.Vector3();
function overCapsule(e) {
capBox.setFromObject(capsule);
let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
for (let i = 0; i < 8; i++) {
capCorner.set(i & 1 ? capBox.max.x : capBox.min.x, i & 2 ? capBox.max.y : capBox.min.y, i & 4 ? capBox.max.z : capBox.min.z).project(camera);
x0 = Math.min(x0, capCorner.x); x1 = Math.max(x1, capCorner.x); y0 = Math.min(y0, capCorner.y); y1 = Math.max(y1, capCorner.y);
}
const r = renderer.domElement.getBoundingClientRect();
const x = (e.clientX - r.left) / r.width * 2 - 1, y = 1 - (e.clientY - r.top) / r.height * 2;
return x >= x0 && x <= x1 && y >= y0 && y <= y1;
}
// Capture phase runs before the OrbitControls wheel listener on the canvas
stage.addEventListener('wheel', e => { controls.enableZoom = zoomAllowed && overCapsule(e); }, { capture: true, passive: true });
stage.addEventListener('pointerdown', () => { controls.enableZoom = zoomAllowed; }, { capture: true });
let flight = null;
function flyTo(key, v = key === 'engr' ? posView() : VIEWS[key]) {
if (mobile() && v.m) v = { ...v, ...v.m };
root.querySelectorAll('.r3d-item').forEach(it => it.classList.toggle('r3d-on', it.dataset.v === key));
shiftTarget = targetShift();
const az0 = controls.getAzimuthalAngle() * 180 / Math.PI;
let daz = v.az - az0; daz = ((daz + 540) % 360) - 180;
flight = {
az0, az1: az0 + daz,
el0: (Math.PI / 2 - controls.getPolarAngle()) * 180 / Math.PI, el1: v.el,
d0: camera.position.distanceTo(controls.target), d1: viewDist(v),
ty0: controls.target.y, ty1: v.ty, tx0: controls.target.x, tx1: v.tx || 0, start: performance.now(), ms: 950,
};
openTarget = v.open; syncToggle();
logoTarget = key === 'engr' ? 1 : 0;
if (key === 'engr') { applyPos(); if (posIdx === 0) armLaser(); else showLogoNow(); } else stopLaser();
if (v === VIEWS.inside) phones.visible = true;
phonesScale.visible = key === 'size';
zoomAllowed = controls.enableZoom = key !== 'size' && key !== 'bottom';
controls.enableRotate = key !== 'bottom';
}
root.querySelectorAll('.r3d-item .r3d-pill').forEach(b => b.addEventListener('click', () => flyTo(b.parentElement.dataset.v)));
function shiftPos(d) {
posIdx = (posIdx + d + POS.length) % POS.length;
applyPos();
const v = posView();
const az0 = controls.getAzimuthalAngle() * 180 / Math.PI;
let daz = v.az - az0; daz = ((daz + 540) % 360) - 180;
flight = { az0, az1: az0 + daz, el0: (Math.PI / 2 - controls.getPolarAngle()) * 180 / Math.PI, el1: v.el, d0: camera.position.distanceTo(controls.target), d1: viewDist(v), ty0: controls.target.y, ty1: v.ty, tx0: controls.target.x, tx1: v.tx || 0, start: performance.now(), ms: 900 };
if (posIdx === 0) armLaser(); else showLogoNow();
}
// Personalization tab is optional (section setting)
root.querySelector('#r3d-pprev')?.addEventListener('click', () => shiftPos(-1));
root.querySelector('#r3d-pnext')?.addEventListener('click', () => shiftPos(1));
root.querySelectorAll('.r3d-chip').forEach(ch => ch.addEventListener('click', () => {
root.querySelectorAll('.r3d-chip').forEach(c => c.classList.remove('r3d-on'));
ch.classList.add('r3d-on');
paints.forEach(m => m.color.setHex(COLORS[ch.dataset.c]));
needRender = true;
tintLogo(ch.dataset.c);
}));
controls.addEventListener('start', () => { flight = null; });
let stageW = stage.clientWidth, stageH = stage.clientHeight;
addEventListener('resize', () => {
if (stage.clientWidth === stageW && stage.clientHeight === stageH) return;
stageW = stage.clientWidth; stageH = stage.clientHeight;
camera.aspect = stage.clientWidth / stage.clientHeight;
camera.updateProjectionMatrix();
renderer.setSize(stage.clientWidth, stage.clientHeight);
shiftY = shiftTarget = targetShift();
applyOffset();
applyZoomLimit();
needRender = true;
setView(controls.getAzimuthalAngle() * 180 / Math.PI, (Math.PI / 2 - controls.getPolarAngle()) * 180 / Math.PI);
renderer.render(scene, camera);
});
let last = performance.now(), onScreen = true, needRender = true;
controls.addEventListener('change', () => { needRender = true; });
document.addEventListener('visibilitychange', () => { if (!document.hidden) needRender = true; });
new IntersectionObserver(e => { onScreen = e[0].isIntersecting; if (onScreen) last = performance.now(); }, { threshold: 0.02 }).observe(stage);
function frame(now = performance.now()) {
requestAnimationFrame(frame);
if (!onScreen) return;
const dt = Math.min((now - last) / 1000, 0.1); last = now;
const k = Math.min((lid.position.y - BASE_H) / 3, 1);
const step = dt / 0.9 * (openTarget < openT ? 0.35 + 0.65 * k * k * (3 - 2 * k) : 1);
openT += Math.sign(openTarget - openT) * Math.min(step, Math.abs(openTarget - openT));
if (openT === 0 && openTarget === 0) phones.visible = false;
const e = openT < 0.5 ? 2 * openT * openT : 1 - Math.pow(-2 * openT + 2, 2) / 2;
lid.position.y = BASE_H + e * LIFT;
if (shiftY !== shiftTarget) {
const ds = shiftTarget - shiftY;
shiftY = Math.abs(ds) < 0.5 ? shiftTarget : shiftY + ds * Math.min(dt * 5, 1);
applyOffset(); needRender = true;
}
logoAlpha += (logoTarget - logoAlpha) * Math.min(dt * (logoTarget ? 12 : 5), 1);
if (laserPending && !flight) startLaser();
laserFrame(now);
sparksFrame(dt);
logoMats.forEach(m => { m.opacity = logoAlpha; });
if (logoAlpha < 0.01) allLogos.forEach(m => m.visible = false);
if (flight) {
const k = Math.min((now - flight.start) / flight.ms, 1), q = 1 - Math.pow(1 - k, 3);
setView(flight.az0 + (flight.az1 - flight.az0) * q, flight.el0 + (flight.el1 - flight.el0) * q, flight.d0 + (flight.d1 - flight.d0) * q, flight.ty0 + (flight.ty1 - flight.ty0) * q, flight.tx0 + (flight.tx1 - flight.tx0) * q);
if (k >= 1) flight = null;
}
lidShell.castShadow = openT < 0.02;
const moved = controls.update();
const animating = flight || laser || laserPending || Math.abs(openTarget - openT) > 1e-4 || Math.abs(logoTarget - logoAlpha) > 1e-3 || sparksAlive;
if (moved || animating || needRender) { renderer.render(scene, camera); needRender = false; }
}
window.__lab = { scene, renderer, key, fill, rim, ground, phones, phonesScale };
frame();
